import { Directive, inject, input, type OnChanges } from '@angular/core';
import { MatTooltip, type TooltipComponent } from '@angular/material/tooltip';
import type { Subscription } from 'rxjs';
import { HoverWarmSession } from '../../services/hover-warm-session';

const INSTANT_CLASS = 'qpx-tooltip-instant';

function withInstantClass(
    value: MatTooltip['tooltipClass']
): TooltipComponent['tooltipClass'] {
    if (typeof value === 'string') {
        return [...value.split(/\s+/).filter(Boolean), INSTANT_CLASS];
    }
    if (Array.isArray(value)) return [...value, INSTANT_CLASS];
    if (value instanceof Set) return [...value, INSTANT_CLASS];
    return { ...value, [INSTANT_CLASS]: true };
}

/** Material tooltip with a shared hover delay and a short warm window. */
@Directive({
    selector: '[qpxTooltip]',
    host: {
        '(focusin)': 'clearPointerEntry()',
        '(mouseenter)': 'markPointerEntry()',
        '(mouseleave)': 'clearPointerEntry()',
        '(touchstart)': 'clearPointerEntry()',
    },
    exportAs: 'qpxTooltip',
})
export class QpxTooltip extends MatTooltip implements OnChanges {
    public readonly qpxTooltip = input<string | number | null | undefined>('');
    private readonly warmth = inject(HoverWarmSession);
    private pointerEntry = false;
    private observedInstance: TooltipComponent | null = null;
    private hiddenSubscription: Subscription | null = null;
    private visibilityObserver: MutationObserver | null = null;

    protected markPointerEntry(): void {
        this.pointerEntry = true;
    }

    protected clearPointerEntry(): void {
        this.pointerEntry = false;
    }

    public ngOnChanges(): void {
        this.message = this.qpxTooltip();
    }

    public override show(
        delay?: number,
        origin?: { x: number; y: number }
    ): void {
        const pointerOpen = this.pointerEntry;
        this.pointerEntry = false;
        const isInstant = pointerOpen && this.warmth.isWarm();
        const chosenDelay = pointerOpen ? this.warmth.hoverDelay() : delay;
        const previous = this.observedInstance;

        super.show(chosenDelay, origin);

        // Angular Material 22.0.5 has no public shown/hidden outputs. Keep the
        // instance integration here, rather than scattering it among callers.
        const instance = this._tooltipInstance;
        if (!instance || instance === previous) return;

        this.stopObserving();
        this.warmth.closed(this);
        this.observedInstance = instance;

        if (isInstant) {
            instance.tooltipClass = withInstantClass(instance.tooltipClass);
            instance._tooltip.nativeElement.classList.add(INSTANT_CLASS);
        }

        this.hiddenSubscription =
            this._overlayRef?.detachments().subscribe(() => {
                if (this.observedInstance !== instance) return;
                this.stopObserving();
                this.warmth.closed(this);
            }) ?? null;

        if (pointerOpen) {
            this.observeVisibility(instance);
        }
    }

    public override hide(delay?: number): void {
        super.hide(delay);
        if (this.observedInstance && !this.observedInstance.isVisible()) {
            this.stopObserving();
        }
    }

    public override ngOnDestroy(): void {
        this.stopObserving();
        this.warmth.closed(this);
        super.ngOnDestroy();
    }

    private observeVisibility(instance: TooltipComponent): void {
        const tooltipElement = instance._tooltip.nativeElement;
        const markOpenedWhenVisible = (): void => {
            if (this.observedInstance !== instance || !instance.isVisible()) {
                return;
            }

            this.visibilityObserver?.disconnect();
            this.visibilityObserver = null;
            this.warmth.opened(this);
        };

        this.visibilityObserver = new MutationObserver(markOpenedWhenVisible);
        this.visibilityObserver.observe(tooltipElement, {
            attributeFilter: ['class'],
            attributes: true,
        });
        markOpenedWhenVisible();
    }

    private stopObserving(): void {
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = null;
        this.hiddenSubscription?.unsubscribe();
        this.hiddenSubscription = null;
        this.observedInstance = null;
    }
}
